import { Close } from '@mui/icons-material';
import CodeIcon from '@mui/icons-material/Code';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Drawer,
    FormControlLabel,
    FormGroup,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { ChangeEvent, FC, useCallback, useState } from 'react';
import { setUnsavedChangesExpiration } from 'src/hooks';
import { useDevTools } from 'src/hooks/devtools/useDevTools';
import { resetToursDatabase } from 'src/hooks/onboarding-tours/useOnboardingTour';
import { useSnackbar } from 'src/hooks/snackbar/useSnackbar';
import pkgJson from '../../../../package.json';
import { DevToolsCopyAuthTokenButton } from './DevToolsCopyAuthTokenButton';

export const DevTools: FC = () => {
    const {
        impersonateEmail,
        setImpersonateEmail,
        isImpersonating,
        setIsImpersonating,
        impersonateRoles,
        setImpersonateRoles,
    } = useDevTools();

    const enqueueSnackbar = useSnackbar();

    const handleImpersonateEmailChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setImpersonateEmail(event.target.value);
        },
        [setImpersonateEmail]
    );

    // Set impersonate roles
    const handleImpersonateRolesChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            const checked = event.target.checked;

            setImpersonateRoles((previousRoles: string[]) => {
                const inx = previousRoles.findIndex((role) => role === value);
                const hasRole = inx >= 0;

                if (hasRole && !checked) {
                    return [...previousRoles.slice(0, inx), ...previousRoles.slice(inx + 1)];
                }

                if (!hasRole && checked) {
                    return [...previousRoles, value];
                }

                return previousRoles;
            });
        },
        [setImpersonateRoles]
    );

    const handleImpersonateClick = () => {
        setIsImpersonating(!isImpersonating);
    };

    const [isOpen, setIsOpen] = useState(false);

    const toggleOpen = useCallback(() => {
        setIsOpen(!isOpen);
    }, [isOpen]);

    const expireUnsavedChanges = useCallback(async () => {
        await setUnsavedChangesExpiration(new Date());
        enqueueSnackbar('Unsaved changes expired', {
            variant: 'success',
            key: 'unsavedChangesExpiredSuccess',
            preventDuplicate: true,
            autoHideDuration: 2000,
        });
    }, [enqueueSnackbar]);

    const resetAllTours = useCallback(() => {
        resetToursDatabase()
            .then(() => {
                enqueueSnackbar('All tours have been reset', {
                    variant: 'success',
                    key: 'resetAllToursSuccess',
                    preventDuplicate: true,
                    autoHideDuration: 2000,
                });
            })
            .catch(() => {
                enqueueSnackbar('Could not reset tours. Please try again later.', {
                    variant: 'error',
                    key: 'resetAllToursError',
                    preventDuplicate: true,
                    autoHideDuration: 2000,
                });
            });
    }, [enqueueSnackbar]);

    return (
        <>
            <Button startIcon={<CodeIcon fontSize="small" />} variant="text" onClick={toggleOpen} color="info">
                DevTools
            </Button>
            <Drawer
                open={isOpen}
                onClose={toggleOpen}
                anchor="right"
                ModalProps={{
                    keepMounted: true,
                }}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: 300,
                        boxSizing: 'border-box',
                    },
                }}
            >
                <Stack
                    sx={{
                        gap: 4,
                        px: 6,
                        py: 4,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="h6">Dev Tools</Typography>
                        <IconButton edge="end" onClick={toggleOpen}>
                            <Close />
                        </IconButton>
                    </Box>
                    <Chip variant="filled" label={`App version: ${pkgJson.version}`} />
                    <TextField
                        data-testid="impersonate-input"
                        size="small"
                        disabled={isImpersonating}
                        label="Impersonate email"
                        placeholder="Enter email address of user to impersonate"
                        value={impersonateEmail}
                        onChange={handleImpersonateEmailChange}
                    />
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={impersonateRoles.includes('superuser')}
                                    value="superuser"
                                    onChange={handleImpersonateRolesChange}
                                />
                            }
                            label="SuperUser"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={impersonateRoles.includes('admin')}
                                    value="admin"
                                    onChange={handleImpersonateRolesChange}
                                />
                            }
                            label="Admin"
                        />
                    </FormGroup>

                    <Button
                        data-testid="impersonate-button"
                        onClick={handleImpersonateClick}
                        variant="contained"
                        color={isImpersonating ? 'error' : 'primary'}
                        disabled={!impersonateEmail}
                    >
                        {isImpersonating ? 'Stop impersonating' : 'Impersonate'}
                    </Button>

                    <DevToolsCopyAuthTokenButton />

                    <Typography variant="h6">Unsaved changes tools</Typography>
                    <Button onClick={expireUnsavedChanges} variant="contained" color="primary">
                        Expire all unsaved changes
                    </Button>

                    <Typography variant="h6">Tour database</Typography>
                    <Button onClick={resetAllTours} variant="contained" color="primary">
                        Reset all tours
                    </Button>
                </Stack>
            </Drawer>
        </>
    );
};
